import React, { useState } from 'react';
import type { Team, Division } from '@sulis-liiga/common';

interface ReRegistrationFormProps {
  previousTeams: Team[];
  availableDivisions: Division[];
  onSubmit: (teamId: string, newSeasonId: string, divisionId?: string) => void;
  onFormNewTeam: () => void;
  loading?: boolean;
  newSeasonId: string;
}

/**
 * ReRegistrationForm component
 *
 * Allows players to re-register an existing team for a new season
 * or navigate to form a new team.
 */
const ReRegistrationForm: React.FC<ReRegistrationFormProps> = ({
  previousTeams,
  availableDivisions,
  onSubmit,
  onFormNewTeam,
  loading = false,
  newSeasonId,
}) => {
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [selectedDivisionId, setSelectedDivisionId] = useState<string>('');
  const [mode, setMode] = useState<'re-register' | 'new-team'>('re-register');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeamId) return;
    onSubmit(selectedTeamId, newSeasonId, selectedDivisionId || undefined);
  };

  const selectedTeam = previousTeams.find(t => t.id === selectedTeamId);

  return (
    <div className="space-y-6">
      {/* Mode selection */}
      <div className="grid grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setMode('re-register')}
          className={`p-4 rounded-lg border-2 text-left transition-colors ${
            mode === 're-register'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <h3 className="text-sm font-semibold text-gray-900">Re-register Existing Team</h3>
          <p className="text-xs text-gray-500 mt-1">
            Continue with your team from the previous season
          </p>
        </button>

        <button
          type="button"
          onClick={() => {
            setMode('new-team');
            onFormNewTeam();
          }}
          className={`p-4 rounded-lg border-2 text-left transition-colors ${
            mode === 'new-team'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <h3 className="text-sm font-semibold text-gray-900">Form New Team</h3>
          <p className="text-xs text-gray-500 mt-1">Start fresh with a new team composition</p>
        </button>
      </div>

      {/* Re-register form */}
      {mode === 're-register' && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Team selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Team to Re-register
            </label>
            {previousTeams.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                You don't have any teams from the previous season.
              </p>
            ) : (
              <select
                value={selectedTeamId}
                onChange={e => setSelectedTeamId(e.target.value)}
                required
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">Select a team...</option>
                {previousTeams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.type})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Team preview */}
          {selectedTeam && (
            <div className="bg-gray-50 rounded-lg p-4 text-sm">
              <h4 className="font-medium text-gray-900 mb-2">{selectedTeam.name}</h4>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <dt className="text-gray-500">Type</dt>
                <dd className="text-gray-700">{selectedTeam.type}</dd>
                <dt className="text-gray-500">Players</dt>
                <dd className="text-gray-700">{selectedTeam.players.length}</dd>
                <dt className="text-gray-500">Enrollments</dt>
                <dd className="text-gray-700">{selectedTeam.enrollments?.length ?? 0}</dd>
              </dl>
            </div>
          )}

          {/* Optional division override */}
          {availableDivisions.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Division (optional - leave blank to keep same division)
              </label>
              <select
                value={selectedDivisionId}
                onChange={e => setSelectedDivisionId(e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">Same as previous season</option>
                {availableDivisions.map(div => (
                  <option key={div.id} value={div.id}>
                    {div.name} (Level {div.level})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!selectedTeamId || loading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Re-registering...
                </>
              ) : (
                'Re-register Team'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ReRegistrationForm;
